import { applySnapshot, flow, getSnapshot, getType, isAlive, isStateTreeNode, types } from 'mobx-state-tree';
import { BaseModel } from './baseModel';
import { ProductOptionModel, ProductOptionModelInstance } from '../productOption';
import { ProductOptionChoiceDetailsInstance } from '../productOptionChoiceDetails';
import { IProductModel } from '../product';
import { parseProductsOptions } from '../parsers';
import { debounce } from 'src/_shared/utils/debounce';
import { LookupCodes } from 'src/constants/lookup.constants';
import { ProductOptionChoiceInstance } from '../productOptionChoice';

export const BaseProductsOptionsModel = BaseModel.named('BaseProductsOptionsModel')
  .props({
    productsOptions: types.optional(types.array(ProductOptionModel), []),
  })
  .views(self => ({
    getProductOptionOf(code: string): ProductOptionModelInstance | undefined {
      return self.productsOptions.find(productOptions => productOptions.code === code);
    },
  }))
  .actions(self => ({
    getProductsOptions(productIds: string[]) {
      return self.awaitSdkProject().then(() => self.sdkProject.getProductsOptions(productIds));
    },
    getQualitySuitableFormats: flow(function* getQualitySuitableFormats(selectedProducts: IProductModel[]) {
      try {
        if (selectedProducts.length === 0) {
          self.productsOptions
            .filter(opt => opt.code === LookupCodes.FORMAT)
            .forEach(opt =>
              opt.choices.forEach((choice: ProductOptionChoiceInstance) => choice.setIsSuboptimal(false)),
            );
          return;
        }
        const product_ids = selectedProducts.map(product => product.id);
        const results: { suitableFormats: Set<string> } = yield self.sdkProject.getQualitySuitableFormatsBulk(product_ids)
          .then(formats => ({
            suitableFormats: new Set<string>(formats.suitableFormats),
          }));

        if (!isAlive(self)) return;

        console.log('getQualitySuitableFormats suitableFormats', results);

        self.productsOptions
          .filter(opt => opt.code === LookupCodes.FORMAT)
          .flatMap(opt => opt.choices as ProductOptionChoiceInstance[])
          .forEach(choice => {
            const isSuboptimal = !results.suitableFormats.has(choice.productOptionChoiceCode);
            choice.setIsSuboptimal(isSuboptimal);
          });
      } catch (err) {
        console.error('Error in getQualitySuitableFormats:', err);
      }
    }),
  }))
  .actions(self => ({
    selectToolOption(
      productOptionCode: string,
      productOptionChoiceCode: string,
      newChoiceDetails?: ProductOptionChoiceDetailsInstance,
    ) {
      const productOption = self.productsOptions.find(productOption => productOption.code === productOptionCode);
      if (productOption) {
        const safeDetails = isStateTreeNode(newChoiceDetails) ? getSnapshot(newChoiceDetails) : newChoiceDetails;
        productOption.choices.forEach(choice => {
          const isSelected = choice.productOptionChoiceCode === productOptionChoiceCode;
          return choice.updateSelected(isSelected, isSelected ? safeDetails as ProductOptionChoiceDetailsInstance : undefined);
        });
      }
    },
    updateProductsOptions: flow(function* updateProductsOptions(selectedProducts) {
      if (self.root.project?.activeGroup?.code) {
        const modelName = getType(self).name;
        const newSelectedProductsIds = selectedProducts.map((product: IProductModel) => product.id);
        const newProductsOptions = yield self.getProductsOptions(newSelectedProductsIds);
        if (!isAlive(self)) return;
        console.log(`#event - DataModel(${modelName}.productsOptions) - event: `, newProductsOptions);
        const productOptionsParsed = parseProductsOptions(newProductsOptions, selectedProducts[0]?.family ?? '');
        applySnapshot(self.productsOptions, productOptionsParsed);
        self.getQualitySuitableFormats(selectedProducts);
        console.log(
          `#event - DataModel(${modelName}.productsOptions) - getSnapshot: `,
          getSnapshot(self.productsOptions),
        );
      }
    }),
  }))
  .actions(self => ({
    updateProductsOptionsDebounced: debounce(self.updateProductsOptions, 100),
  }));
